// Import et export des personnes depuis un fichier Excel (.xlsx) ou CSV.
import type { Person } from '../../../supabase/functions/_shared/core/index.ts';
import {
  type ImportEntry,
  peopleToRows,
  planImport,
  readXlsx,
  templateRows,
  writeXlsx,
} from '../../../supabase/functions/_shared/core/index.ts';
import { busy, h, mount, toast } from '../dom.ts';
import { uuid } from '../api.ts';
import { reload, type Store } from '../state.ts';
import { openModal } from './modal.ts';

const ACTION_LABEL: Record<ImportEntry['action'], string> = {
  create: 'Création',
  update: 'Mise à jour',
  unchanged: 'Inchangée',
  error: 'Refusée',
};
const ACTION_CLASS: Record<ImportEntry['action'], string> = {
  create: 'live',
  update: 'upcoming',
  unchanged: '',
  error: 'bad',
};

function download(name: string, data: Uint8Array | string, mime: string): void {
  const blob = new Blob([data as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function exportPeople(store: Store): void {
  const rows = store.people.length ? peopleToRows(store.people, store.brands) : templateRows(store.brands);
  const day = new Date().toISOString().slice(0, 10);
  download(
    `signatures-personnes-${day}.xlsx`,
    writeXlsx(rows, 'Personnes'),
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
  toast(
    store.api.demo
      ? 'Téléchargement lancé — il peut être bloqué dans la démo publiée ; il fonctionne sur l’outil installé.'
      : 'Fichier Excel téléchargé',
  );
}

export function openImportModal(store: Store, refresh: () => void): void {
  const file = h('input', { type: 'file', accept: '.xlsx,.csv,.tsv,text/csv' });
  const report = h('div', { class: 'import-report' });
  const apply = h('button', { disabled: true }, 'Importer');
  let toApply: ImportEntry[] = [];

  const close = openModal(
    'Importer des personnes',
    h(
      'div',
      null,
      h(
        'p',
        { class: 'muted' },
        'Fichier Excel (.xlsx) ou CSV. La première ligne doit contenir les en-têtes : Adresse mail, Prénom, Nom, Fonction, Mobile, Fixe, Lieu (charte)… ' +
          'L’ordre des colonnes est libre et les colonnes en trop sont ignorées. Une cellule vide ne change rien ; écrire « - » efface la valeur.',
      ),
      h(
        'p',
        { class: 'muted' },
        'Le plus simple : exporter la liste, la compléter dans Excel, puis la réimporter. L’import ne crée aucune boîte dans Google Workspace : ' +
          'les comptes doivent exister, l’import ne fait que renseigner leurs informations.',
      ),
      h(
        'div',
        { class: 'row' },
        file,
        h(
          'button',
          { class: 'ghost', onclick: () => exportPeople(store) },
          'Exporter la liste (Excel)',
        ),
      ),
      report,
    ),
    [apply],
  );

  file.addEventListener('change', () =>
    busy(null, async () => {
      const f = file.files?.[0];
      if (!f) return;
      mount(report, h('p', { class: 'muted' }, 'Lecture du fichier…'));
      const rows = /\.xlsx$/i.test(f.name)
        ? await readXlsx(new Uint8Array(await f.arrayBuffer()))
        : (await import('../../../supabase/functions/_shared/core/xlsx.ts'))
          .parseCsv(await f.text());
      const plan = planImport(rows, store.people, store.brands);
      toApply = plan.entries.filter((e) => e.action === 'create' || e.action === 'update');
      apply.disabled = toApply.length === 0;
      apply.textContent = toApply.length ? `Importer ${toApply.length} ligne(s)` : 'Rien à importer';

      const ignored = plan.columns.filter((c) => !c.field).map((c) => c.header);
      mount(
        report,
        h(
          'div',
          { class: 'meta' },
          (['create', 'update', 'unchanged', 'error'] as const).filter((k) => plan.counts[k]).map((k) =>
            h(
              'span',
              { class: `chip ${ACTION_CLASS[k]}` },
              `${ACTION_LABEL[k]} : ${plan.counts[k]}`,
            )
          ),
        ),
        ignored.length
          ? h(
            'p',
            { class: 'muted' },
            `Colonnes ignorées : ${ignored.join(', ')}`,
          )
          : null,
        h(
          'div',
          { class: 'table-wrap scroll' },
          h(
            'table',
            null,
            h(
              'thead',
              null,
              h(
                'tr',
                null,
                ['Ligne', 'Adresse', 'Résultat', 'Détail'].map((t) => h('th', null, t)),
              ),
            ),
            h(
              'tbody',
              null,
              plan.entries.map((e) =>
                h(
                  'tr',
                  { class: e.action === 'error' ? 'error' : '' },
                  h('td', null, String(e.line)),
                  h('td', null, e.email || '—'),
                  h(
                    'td',
                    null,
                    h(
                      'span',
                      { class: `chip ${ACTION_CLASS[e.action]}` },
                      ACTION_LABEL[e.action],
                    ),
                  ),
                  h(
                    'td',
                    null,
                    e.problems.length ? e.problems.join(' · ') : [
                      e.changes.map((c) => `${c.label} : ${c.from} → ${c.to}`)
                        .join(' · '),
                      e.warnings.length ? h('div', { class: 'warn' }, e.warnings.join(' ')) : null,
                    ],
                  ),
                )
              ),
            ),
          ),
        ),
      );
    }));

  apply.addEventListener('click', () =>
    busy(apply, async () => {
      let done = 0;
      const failures: string[] = [];
      for (const entry of toApply) {
        const isNew = entry.action === 'create';
        const person: Person = isNew
          ? {
            ...entry.person!,
            id: uuid(),
            install_token: uuid().replace(/-/g, ''),
            public_token: uuid().replace(/-/g, ''),
          }
          : entry.person!;
        try {
          await store.api.savePerson(person, isNew);
          done++;
        } catch (e) {
          failures.push(
            `${entry.email} : ${e instanceof Error ? e.message : String(e)}`,
          );
        }
      }
      await reload(store);
      close();
      refresh();
      toast(
        failures.length
          ? `${done} ligne(s) importée(s), ${failures.length} en échec : ${failures[0]}`
          : `${done} ligne(s) importée(s)${store.api.demo ? ' (démo)' : ' — appliquées à la prochaine poussée'}`,
        failures.length ? 'error' : 'ok',
      );
    }));
}
